import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { OperatorType } from "@/data/operatorTypes";

interface Profile {
  id: string;
  user_id: string;
  full_name: string | null;
  email: string | null;
  operator_type: OperatorType | null;
  operator_type_updated_at: string | null;
}

export interface UserProfileState {
  session: Session | null;
  profile: Profile | null;
  /** True only until we have determined whether a session exists.
   * Decoupled from profile fetching so a slow / hung profile query
   * cannot leave the spinner up forever. */
  loading: boolean;
  /** True while the profile row is being (re)fetched. Separate from
   * `loading` so callers that need to wait on the profile specifically
   * (e.g. the Layout's operator-type onboarding guard) can do so without
   * changing the session-loading semantics. */
  profileLoading: boolean;
  /** Force a re-fetch of the profile row. Used after mutations like
   * the operator-type onboarding save so the guard stops redirecting. */
  refreshProfile: () => Promise<void>;
}

const DEFAULT_STATE: UserProfileState = {
  session: null,
  profile: null,
  loading: true,
  profileLoading: false,
  refreshProfile: async () => {},
};

const UserProfileContext = createContext<UserProfileState>(DEFAULT_STATE);

/** Hard timeout: if neither auth path resolves within this window, we give
 *  up and assume "no session" so the spinner can clear. The user can still
 *  sign in normally — we just refuse to block the entire app on a hung
 *  Supabase auth call. */
const AUTH_RESOLVE_TIMEOUT_MS = 3000;

const PROFILE_COLUMNS =
  "id, user_id, full_name, email, operator_type, operator_type_updated_at";

/**
 * Single source of truth for the authenticated user's session + profile.
 *
 * Mounted ONCE at the app root via <UserProfileProvider>. Every consumer
 * calls useUserProfile() which reads from the shared context — there is no
 * per-component auth subscription, no per-component getSession call, and
 * no per-component fetchProfile race.
 *
 * Critical design rule: `loading` reflects ONLY whether we have determined
 * the session yet. It is NOT coupled to the profile fetch. As soon as we
 * know "no session" or "this session", loading flips to false and the
 * Layout's spinner clears. The profile loads in the background and updates
 * state when ready. This means a slow or stuck profile query can never
 * block the page from rendering. If a specific caller needs to wait on
 * the profile (like the operator-type onboarding guard), it should check
 * `profileLoading` instead.
 */
export function UserProfileProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<
    Omit<UserProfileState, "refreshProfile">
  >({
    session: null,
    profile: null,
    loading: true,
    profileLoading: false,
  });

  // Mirror of the current session so refreshProfile can read the user id
  // without being recreated on every render.
  const sessionRef = useRef<Session | null>(null);
  const mountedRef = useRef(true);

  const fetchProfileFor = useCallback(async (userId: string) => {
    setState((prev) => ({ ...prev, profileLoading: true }));
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select(PROFILE_COLUMNS)
        .eq("user_id", userId)
        .maybeSingle();
      if (!mountedRef.current) return;
      if (error) {
        console.warn("[useUserProfile] profile fetch error:", error);
        setState((prev) => ({ ...prev, profileLoading: false }));
        return;
      }
      setState((prev) => ({
        ...prev,
        profile: (data as Profile) ?? null,
        profileLoading: false,
      }));
    } catch (err) {
      if (!mountedRef.current) return;
      console.warn("[useUserProfile] profile fetch threw:", err);
      setState((prev) => ({ ...prev, profileLoading: false }));
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    const userId = sessionRef.current?.user?.id;
    if (!userId) return;
    await fetchProfileFor(userId);
  }, [fetchProfileFor]);

  useEffect(() => {
    mountedRef.current = true;
    let resolved = false;

    /** Set session immediately, then fetch profile in the background.
     *  loading flips to false on the FIRST setState, not after the profile
     *  fetch. */
    function applySession(session: Session | null) {
      if (!mountedRef.current) return;
      resolved = true;
      sessionRef.current = session;
      setState((prev) => ({
        session,
        profile: session ? prev.profile : null, // clear profile on sign-out
        loading: false,
        profileLoading: !!session,
      }));
      if (session) {
        void fetchProfileFor(session.user.id);
      }
    }

    // Subscribe FIRST so we don't miss any in-flight auth events.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Wipe every cached query on sign-out so one user's RLS-scoped
      // data (votes, watchlists, alerts, decisions, spotlight reads)
      // can never bleed into the next session on the same device.
      if (event === "SIGNED_OUT") {
        queryClient.clear();
      }
      applySession(session);
    });

    // Seed from the persisted session.
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (resolved) return; // onAuthStateChange may have fired first
        applySession(session);
      })
      .catch((err) => {
        console.warn("[useUserProfile] getSession failed:", err);
        if (!resolved) applySession(null);
      });

    // Hard timeout safety net. If neither path resolves within 3 seconds,
    // assume no session so the spinner clears and the user can interact
    // with the page (or hit /auth themselves).
    const timeoutId = setTimeout(() => {
      if (!resolved && mountedRef.current) {
        console.warn(
          "[useUserProfile] auth resolve timeout — assuming no session.",
        );
        applySession(null);
      }
    }, AUTH_RESOLVE_TIMEOUT_MS);

    return () => {
      mountedRef.current = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [fetchProfileFor, queryClient]);

  const contextValue = useMemo<UserProfileState>(() => ({
    ...state,
    refreshProfile,
  }), [state, refreshProfile]);

  return (
    <UserProfileContext.Provider value={contextValue}>
      {children}
    </UserProfileContext.Provider>
  );
}

/**
 * Read the current user's session + profile from the shared context.
 * Same return shape as the legacy hook so existing callers don't change.
 */
export function useUserProfile(): UserProfileState {
  return useContext(UserProfileContext);
}
